<script>
import { userMethods, authComputed } from '@state/helpers'
import _ from 'lodash'

export default {
  data() {
    return {
      search: '',
      loadingError: null,
      formError: null,
      loading: true,
      dialog: false,
      pagination: {
        rowsPerPage: 25,
      },
      editedIndex: -1,
      items: [],
      totalData: 0,
      isWorking: false,
      headers: [
        {
          text: 'First Name',
          value: 'firstName',
        },
        {
          text: 'Last Name',
          value: 'lastName',
        },
        {
          text: 'Username',
          value: 'username',
        },
        {
          text: 'Active',
          value: 'active',
        },
        {
          text: 'Admin',
          value: 'isAdmin',
        },
        { text: 'Actions', value: 'firstName', sortable: false },
      ],
      editedItem: {
        firstName: null,
        lastName: null,
        username: null,
        isAdmin: false,
        active: false,
      },
      defaultItem: {
        firstName: null,
        lastName: null,
        username: null,
        isAdmin: false,
        active: false,
      },
    }
  },
  computed: {
    ...authComputed,
    formTitle() {
      return this.editedIndex === -1
        ? 'Invite New User'
        : `Edit ${this.items[this.editedIndex].username}`
    },
  },
  watch: {
    pagination: {
      handler() {
        this.getDataFromApi()
      },
      deep: true,
    },
    dialog(val) {
      val || this.close()
    },
  },
  created() {
    this.getDataFromApi = _.debounce(this.getDataFromApi, 500)
  },
  methods: {
    ...userMethods,
    getDataFromApi() {
      this.loading = true
      var vm = this
      return this.getUsers({
        queryTerms: { ...this.pagination, filter: this.search },
      })
        .then(data => {
          vm.loading = false

          vm.totalData = data.totalCount
          vm.items = data.results
        })
        .catch(error => {
          vm.loading = false
          vm.loadingError = error
        })
    },
    save() {
      this.isWorking = true
      this.formError = null

      if (this.editedIndex !== -1) {
        // Update user
        this.updateUser({ user: this.editedItem })
          .then(response => {
            this.isWorking = false
            var user = response.data
            var matches = this.items.filter(x => x.id === user.id)
            if (matches.length) {
              var current = matches[0]
              var indexToReplace = this.items.indexOf(current)
              this.items[indexToReplace] = user
            }
            this.dialog = false
          })
          .catch(error => {
            this.isWorking = false
            this.formError = error.response.data
          })
      } else {
        // Add new
        this.inviteUser({ user: this.editedItem })
          .then(response => {
            this.isWorking = false
            var user = response.data
            this.items.unshift(user)
            this.dialog = false
          })
          .catch(error => {
            this.isWorking = false
            this.formError = error.response.data
          })
      }
    },
    close() {
      this.dialog = false
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      }, 300)
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },
  },
}
</script>

<template>
  <v-card>
    <!-- Table header -->
    <v-toolbar flat color="transparent">
      <v-toolbar-title>Users</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-spacer></v-spacer>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Search"
        color="cyan"
        single-line
        hide-details
      ></v-text-field>

      <!-- Invite user dialog -->
      <v-dialog v-model="dialog" max-width="500px">
        <v-btn slot="activator" color="cyan" dark depressed class="mb-2 ml-4"
          >Invite User</v-btn
        >
        <v-card class="px-3 py-2">
          <v-card-title>
            <span class="headline">{{ formTitle }}</span>
          </v-card-title>

          <v-card-text>
            <v-container grid-list-md class="pa-0">
              <v-layout wrap>
                <v-flex xs12 sm12 md12>
                  <v-text-field
                    v-if="!editedItem.id"
                    v-model="editedItem.username"
                    type="email"
                    label="Email Address"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.firstName"
                    label="First Name"
                    color="cyan"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.lastName"
                    label="Last Name"
                    color="cyan"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-checkbox
                    v-if="editedItem.id && currentUser.id !== editedItem.id"
                    v-model="editedItem.active"
                    label="Active"
                    color="cyan"
                  ></v-checkbox>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-checkbox
                    v-model="editedItem.isAdmin"
                    label="Admin"
                    color="cyan"
                  ></v-checkbox>
                </v-flex>
                <v-alert :value="formError" type="error">
                  {{ formError }}
                </v-alert>
              </v-layout>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="cyan" dark depressed outline @click="close"
              >Cancel</v-btn
            >
            <v-btn color="cyan" dark depressed @click="save">
              <BaseIcon v-if="isWorking" name="sync" spin />
              <span v-else>Save</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>

    <!-- Table -->
    <v-data-table
      :headers="headers"
      :items="items"
      :pagination.sync="pagination"
      :total-items="totalData"
      :loading="loading"
      :search="search"
      class="elevation-1"
    >
      <v-progress-linear
        slot="progress"
        color="blue"
        indeterminate
      ></v-progress-linear>
      <template slot="items" slot-scope="props">
        <td>{{ props.item.firstName }}</td>
        <td>{{ props.item.lastName }}</td>
        <td>{{ props.item.username }}</td>
        <td>
          <v-icon v-if="props.item.active" size="x-small" color="green"
            >mdi-circle</v-icon
          >
          <v-icon v-else size="x-small" color="red">mdi-circle</v-icon>
        </td>
        <td>
          <v-icon v-if="props.item.isAdmin" size="x-small" color="green"
            >mdi-circle</v-icon
          >
          <v-icon v-else size="x-small" color="red">mdi-circle</v-icon>
        </td>
        <td>
          <v-icon small class="mr-2" @click="editItem(props.item)">edit</v-icon>
        </td>
      </template>
    </v-data-table>
  </v-card>
</template>
